import { useState, useEffect } from 'react';
import { useShopStore } from '../store/useShopStore';
import { ASSET_CATEGORIES } from '../config/constants';
import SpriteImporter, { loadCustomSprites, deleteCustomSprite, clearAllCustomSprites } from './SpriteImporter';

// Default asset manifest (placeholder until real assets are loaded)
const BUILTIN_ASSETS = {
  'machine-lathe': {
    key: 'machine-lathe',
    category: ASSET_CATEGORIES.MACHINE,
    name: 'CNC Lathe',
    footprint: { w: 2, h: 2 },
    color: 0x6366f1,
  },
  'machine-mill': {
    key: 'machine-mill',
    category: ASSET_CATEGORIES.MACHINE,
    name: 'Vertical Mill',
    footprint: { w: 2, h: 3 },
    color: 0x8b5cf6,
  },
  'machine-grinder': {
    key: 'machine-grinder',
    category: ASSET_CATEGORIES.MACHINE,
    name: 'Surface Grinder',
    footprint: { w: 2, h: 1 },
    color: 0xa855f7,
  },
  'prop-workbench': {
    key: 'prop-workbench',
    category: ASSET_CATEGORIES.PROP,
    name: 'Workbench',
    footprint: { w: 2, h: 1 },
    color: 0x78716c,
  },
  'prop-toolcart': {
    key: 'prop-toolcart',
    category: ASSET_CATEGORIES.PROP,
    name: 'Tool Cart',
    footprint: { w: 1, h: 1 },
    color: 0x57534e,
  },
  'prop-shelf': {
    key: 'prop-shelf',
    category: ASSET_CATEGORIES.PROP,
    name: 'Storage Shelf',
    footprint: { w: 1, h: 2 },
    color: 0x44403c,
  },
};

// Combined assets (builtin + custom) - starts with builtins
let DEFAULT_ASSETS = { ...BUILTIN_ASSETS };

// Function to get current assets (always fresh)
export function getAssets() {
  return DEFAULT_ASSETS;
}

// Function to get a specific asset
export function getAsset(key) {
  return DEFAULT_ASSETS[key];
}

// Function to refresh assets with custom sprites
function refreshAssets(customSprites) {
  DEFAULT_ASSETS = { ...BUILTIN_ASSETS };
  customSprites.forEach((sprite) => {
    DEFAULT_ASSETS[sprite.id] = {
      key: sprite.id,
      category: sprite.category === 'machines' ? ASSET_CATEGORIES.MACHINE : 
                sprite.category === 'props' ? ASSET_CATEGORIES.PROP : 'custom',
      name: sprite.name,
      footprint: sprite.footprint,
      color: sprite.color,
      sprite: sprite.sprite, // base64 image data
      isCustom: true,
    };
  });
}

// Initialize with any stored custom sprites on module load
const storedSprites = loadCustomSprites();
if (storedSprites.length > 0) {
  refreshAssets(storedSprites);
}

export default function Palette({ collapsed, onToggle }) {
  const { selectedAssetKey, setSelectedAsset, currentTool } = useShopStore();
  const [showImporter, setShowImporter] = useState(false);
  const [customSprites, setCustomSprites] = useState([]);

  // Load custom sprites on mount
  useEffect(() => {
    const sprites = loadCustomSprites();
    setCustomSprites(sprites);
    refreshAssets(sprites);
  }, []);

  // Handle new sprite import
  const handleImport = (newSprite) => {
    const updatedSprites = [...customSprites, newSprite];
    setCustomSprites(updatedSprites);
    refreshAssets(updatedSprites);
  };

  // Handle delete custom sprite
  const handleDeleteSprite = (spriteId) => {
    const updatedSprites = deleteCustomSprite(spriteId);
    setCustomSprites(updatedSprites);
    refreshAssets(updatedSprites);
    // Deselect if currently selected
    if (selectedAssetKey === spriteId) {
      setSelectedAsset(null);
    }
  };
  
  // Handle clear all custom sprites
  const handleClearAll = () => {
    clearAllCustomSprites();
    setCustomSprites([]);
    refreshAssets([]);
    setSelectedAsset(null);
  };

  const assetsByCategory = Object.values(DEFAULT_ASSETS).reduce((acc, asset) => {
    const cat = asset.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(asset);
    return acc;
  }, {});

  const categoryLabels = {
    [ASSET_CATEGORIES.MACHINE]: 'Machines',
    [ASSET_CATEGORIES.PROP]: 'Props',
    [ASSET_CATEGORIES.FLOOR]: 'Floors',
    [ASSET_CATEGORIES.WALL]: 'Walls',
    [ASSET_CATEGORIES.DECAL]: 'Decals',
    'custom': 'Custom',
  };

  // Collapsed state - just show toggle button
  if (collapsed) {
    return (
      <div className="w-10 h-full bg-gray-800/95 backdrop-blur-sm border-r border-gray-700 flex flex-col items-center py-2">
        <button
          onClick={onToggle}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          title="Show Asset Palette"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="w-64 h-full bg-gray-800/95 backdrop-blur-sm border-r border-gray-700 flex flex-col">
      <div className="p-3 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggle}
              className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Collapse"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-sm font-semibold text-gray-300">Assets</h2>
          </div>
          <div className="flex items-center gap-1">
            {customSprites.length > 0 && (
              <button
                onClick={() => {
                  if (confirm(`Delete all ${customSprites.length} custom sprites?`)) {
                    handleClearAll();
                  }
                }}
                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
                title="Clear all custom sprites"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setShowImporter(true)}
              className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-gray-700 rounded transition-colors"
              title="Import sprite"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {Object.entries(assetsByCategory).map(([category, assets]) => (
          <div key={category} className="mb-4">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-1">
              {categoryLabels[category] || category}
            </h3>
            <div className="space-y-1">
              {assets.map((asset) => (
                <AssetItem
                  key={asset.key}
                  asset={asset}
                  selected={selectedAssetKey === asset.key && currentTool === 'place'}
                  onClick={() => {
                    if (selectedAssetKey === asset.key) {
                      setSelectedAsset(null);
                    } else {
                      setSelectedAsset(asset.key);
                    }
                  }}
                  onDelete={asset.isCustom ? () => handleDeleteSprite(asset.key) : null}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {currentTool === 'place' && selectedAssetKey && (
        <div className="p-3 border-t border-gray-700 bg-indigo-900/20">
          <p className="text-xs text-indigo-300">
            Double-click to place <strong>{DEFAULT_ASSETS[selectedAssetKey]?.name}</strong>
          </p>
        </div>
      )}

      {/* Sprite Importer Modal */}
      {showImporter && (
        <SpriteImporter
          onImport={handleImport}
          onClose={() => setShowImporter(false)}
        />
      )}
    </div>
  );
}

function AssetItem({ asset, selected, onClick, onDelete }) {
  return (
    <div
      className={`w-full flex items-center gap-3 p-2 rounded transition-colors text-left group ${
        selected
          ? 'bg-blue-600 text-white'
          : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
      }`}
    >
      <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0">
        {/* Color swatch or sprite preview */}
        {asset.sprite ? (
          <img
            src={asset.sprite}
            alt={asset.name}
            className="w-8 h-8 rounded flex-shrink-0 object-contain bg-gray-900"
          />
        ) : (
          <div
            className="w-8 h-8 rounded flex-shrink-0"
            style={{ backgroundColor: `#${asset.color.toString(16).padStart(6, '0')}` }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{asset.name}</div>
          <div className="text-xs text-gray-500">
            {asset.footprint.w}x{asset.footprint.h}
          </div>
        </div>
      </button>
      {/* Delete button for custom assets */}
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Delete "${asset.name}"?`)) {
              onDelete();
            }
          }}
          className="p-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
          title="Delete custom sprite"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
}

// Note: Use getAsset() or getAssets() for dynamic access to assets
// DEFAULT_ASSETS is kept for backwards compatibility but prefer the functions
